// src/lib/displayTimezone.js
//
// Display-timezone helpers for booking.
//
// The instant always comes from the API in UTC. The zone only decides how it
// is written and which local dates a search covers, so changing the zone never
// moves a slot. There is no hardcoded country fallback: if the browser cannot
// report a zone, nothing is sent and the API applies its documented default.

export const MAX_RANGE_DAYS = 31;

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export function isValidTimezone(timeZone) {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** yyyy-mm-dd for "today" as seen in `timeZone` (not the UTC date). */
export function todayInZone(timeZone, now = new Date()) {
  const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  if (isValidTimezone(timeZone)) options.timeZone = timeZone;
  const parts = new Intl.DateTimeFormat("en-CA", options).formatToParts(now);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Calendar arithmetic on a yyyy-mm-dd string. Zone-free, so DST cannot shift it. */
export function addDaysIso(isoDate, days) {
  const [year, month, day] = String(isoDate).split("-").map(Number);
  const date = new Date(Date.UTC(year, (month || 1) - 1, (day || 1) + days));
  return date.toISOString().slice(0, 10);
}

export function daysBetweenInclusive(fromIso, toIso) {
  const [fy, fm, fd] = String(fromIso).split("-").map(Number);
  const [ty, tm, td] = String(toIso).split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000) + 1;
}

export function defaultDateRange(timeZone, now = new Date()) {
  const from = todayInZone(timeZone, now);
  return { dateFrom: from, dateTo: addDaysIso(from, MAX_RANGE_DAYS - 1) };
}

/** A readable problem with a local date range, or "" when it is valid. */
export function dateRangeProblem(fromIso, toIso) {
  if (!fromIso || !toIso) return "";
  if (toIso < fromIso) return "The end date must be on or after the start date.";
  if (daysBetweenInclusive(fromIso, toIso) > MAX_RANGE_DAYS) return `Search up to ${MAX_RANGE_DAYS} days at a time.`;
  return "";
}

function format(value, timeZone, options) {
  if (!value) return "—";
  const resolved = { ...options };
  if (isValidTimezone(timeZone)) resolved.timeZone = timeZone;
  try {
    return new Intl.DateTimeFormat("en-CA", resolved).format(new Date(value));
  } catch {
    return "—";
  }
}

export function formatDateTimeInZone(value, timeZone) {
  return format(value, timeZone, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatTimeInZone(value, timeZone) {
  return format(value, timeZone, { hour: "numeric", minute: "2-digit" });
}

/** A yyyy-mm-dd local date written for people. Parsed as a calendar date, never as an instant. */
export function formatLocalDate(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-").map(Number);
  try {
    return new Intl.DateTimeFormat("en-CA", { weekday: "short", year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }).format(
      new Date(Date.UTC(year, month - 1, day))
    );
  } catch {
    return isoDate;
  }
}
